import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import TeamManagement from './TeamManagement';
import CompanySettings from './CompanySettings';
import UserPreferences from './UserPreferences';
import Billing from './Billing';
import Integrations from './Integrations';
import Security from './Security';
import SystemHealth from './SystemHealthEnhanced';
import CostingControlSheet from './CostingControlSheet';
import DataMaintenance from './DataMaintenance';
import { Users, Building2, UserCog, CreditCard, Plug, ShieldCheck, Activity, Calculator, Wrench } from 'lucide-react';

const SettingsPage = () => {
  const { role } = useAuth();
  const [activeTab, setActiveTab] = useState('team');
  
  // Check if user is Owner or Admin
  const isOwner = role === 'Owner';
  const isAdmin = role === 'Admin' || isOwner;
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Card className="max-w-md bg-gray-800 border-gray-700">
          <CardHeader className="p-3">
            <CardTitle className="text-sm text-red-400">Access Denied</CardTitle>
            <CardDescription className="text-xs text-gray-400">
              You don't have permission to access Settings. Only Owners and Admins can manage team settings.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-3">
      <div className="max-w-7xl mx-auto">
        {/* Page Header - Compact */}
        <div className="mb-3">
          <h1 className="text-lg font-bold text-white">Settings</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            Manage your team, company settings, and preferences
          </p>
        </div>

        {/* Tabs - Compact with border-bottom active indicator */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full gap-0 mb-3 bg-transparent p-0 h-auto border-b border-gray-700">
            <TabsTrigger 
              value="team" 
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 rounded-none bg-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-400 hover:text-white transition-colors"
            >
              <Users className="w-3 h-3" />
              <span>Team</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="company" 
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 rounded-none bg-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-400 hover:text-white transition-colors"
            >
              <Building2 className="w-3 h-3" />
              <span>Company</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="preferences" 
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 rounded-none bg-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-400 hover:text-white transition-colors"
            >
              <UserCog className="w-3 h-3" />
              <span>Preferences</span>
            </TabsTrigger>
            
            {isOwner && (
              <TabsTrigger 
                value="billing" 
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 rounded-none bg-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-400 hover:text-white transition-colors"
              >
                <CreditCard className="w-3 h-3" />
                <span>Billing</span>
              </TabsTrigger>
            )}
            
            <TabsTrigger 
              value="integrations" 
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 rounded-none bg-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-400 hover:text-white transition-colors"
            >
              <Plug className="w-3 h-3" />
              <span>Integrations</span>
            </TabsTrigger>
            
            {isOwner && (
              <TabsTrigger 
                value="security" 
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 rounded-none bg-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-400 hover:text-white transition-colors"
              >
                <ShieldCheck className="w-3 h-3" />
                <span>Security</span>
              </TabsTrigger>
            )}
            
            <TabsTrigger 
              value="health" 
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 rounded-none bg-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-400 hover:text-white transition-colors"
            >
              <Activity className="w-3 h-3" />
              <span>System Health</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="costing" 
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 rounded-none bg-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-400 hover:text-white transition-colors"
              data-testid="costing-tab"
            >
              <Calculator className="w-3 h-3" />
              <span>Costing</span>
            </TabsTrigger>
            
            <TabsTrigger 
              value="maintenance" 
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 rounded-none bg-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-400 hover:text-white transition-colors"
              data-testid="maintenance-tab"
            >
              <Wrench className="w-3 h-3" />
              <span>Data Maintenance</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab Content */}
          <TabsContent value="team" className="mt-0">
            <TeamManagement />
          </TabsContent>

          <TabsContent value="company" className="mt-0">
            <CompanySettings />
          </TabsContent>

          <TabsContent value="preferences" className="mt-0">
            <UserPreferences />
          </TabsContent>

          {isOwner && (
            <TabsContent value="billing" className="mt-0">
              <Billing />
            </TabsContent>
          )}

          <TabsContent value="integrations" className="mt-0">
            <Integrations />
          </TabsContent>

          {isOwner && (
            <TabsContent value="security" className="mt-0">
              <Security />
            </TabsContent>
          )}

          <TabsContent value="health" className="mt-0">
            <SystemHealth />
          </TabsContent>

          <TabsContent value="costing" className="mt-0">
            <CostingControlSheet />
          </TabsContent>
          
          <TabsContent value="maintenance" className="mt-0">
            <DataMaintenance />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SettingsPage;
